import { Dispatch, SetStateAction } from 'react'
import type { RowData } from '@tanstack/react-table'
import type { ColumnDefinition, Row as TableRow } from './components/common/table/tableFeatures'
import Report from './models/Report'

export enum SupervisorRole {
  CoordinatingAcademic = 'coordinating-academic',
  MainSupervisor = 'main-supervisor',
  Supervisor = 'supervisor'
}

export const ThesisCommitteeMemberRole = 'thesis-committee-member'

export type MyDoctoralCandidateRole = SupervisorRole | typeof ThesisCommitteeMemberRole

export type InvitationToRole =
  'coordinating-academic' | 'main-supervisor' | 'supervisor' | typeof ThesisCommitteeMemberRole

export type Invitation = {
  id: number
  inviter: string
  roles: InvitationToRole[]
  token: string
  doctoralProgramme: TranslatedString
  titleOfDissertation: string
}

export type Reminder = {
  description: string
  due_at: string
  id: number
  isActive: boolean
  pivot: Pivot
  reminding_schedule: RemindingSchedule[]
  title: string
  users: Recipient[]
}

type RemindingSchedule = {
  id: number
  is_reminder_sent: boolean
  send_reminder_at: string
}

export type LoginWithTokenResponse = {
  studentNumber: string
}

export type AcceptInvitationResponse = {
  studentNumber: string
}

export type DoctoralProgramme = {
  id: number
  name: TranslatedString
  code: string
}

export type User = {
  id: number
  isAccountClosed: boolean
  name: string
  roles: string[]
  doctoralProgrammes: number[]
  closingReason?: ClosingReason
  consortium?: string
}

export type ClosingReason = {
  id: number
  description: string
}

export type AuthenticatedUser = {
  closedAt: string
  doctoralCandidate: UserDetailsDoctoralCandidate
  coordinator: UserDetailsCoordinator
  email: string
  firstName: string
  id: number
  isActivated: boolean
  isForeman: boolean
  isSupervisor: boolean
  isThesisCommitteeMember: boolean
  lastName: string
  onboardingLevel: number
  username: string
}

export type Supervisor = {
  id: number
  name: string
  email: string
  roles: SupervisorRole[]
}

export type UserDetails = {
  coordinator: UserDetailsCoordinator
  doctoralCandidate: UserDetailsDoctoralCandidate
  doctoralCandidateReports: Report[]
  supervisor: UserDetailsSupervisor
  thesisCommitteeMember: UserDetailsThesisCommitteeMember
  user: UserDetailsUser
}

type UserDetailsCoordinator = {
  id: number
}

export type UserDetailsDoctoralCandidate = {
  beginningOfStudies: string
  doctoralProgramme: string
  faculty: string
  id: number
  isFastDoctoralCandidate: boolean
  major: string
  studentNumber: string
  supervisorRelationships: UserDoctoralCandidateSupervisorRelationship[]
  thesisCommitteeMembers: UserDoctoralCandidateThesisCommitteeMember[]
  verifiedStudentNumber: string
}

type UserDetailsSupervisor = {
  id: number
}

type UserDetailsThesisCommitteeMember = {
  id: number
}

type UserDetailsUser = {
  closingReason: { id: number; description: 'Graduated' | 'Discontinued' }
  email: string
  firstName: string
  hyUsername: string
  id: number
  isAccountClosed: boolean
  lastName: string
  signedUpToThessaAt: string
  organization?: string
}

export type Grant = {
  [key: string]: any // eslint-disable-line @typescript-eslint/no-explicit-any -- dynamic field access
  id: number
}

export type TeachingType = 'course_or_workshop' | 'thesis_instructor'

export type Teaching = {
  id: number
  beginsAt: string
  courseName: string
  endsAt: string
  myRole: string
  studentName: string
  thesisLevel: string
  thesisTopicOrTitle: string
  type: TeachingType
  workHours: number
}

export type Roles = {
  isRequesterCoordinator: boolean
  isRequesterDoctoralCandidate: boolean
  isRequesterMemberOfThesisCommittee: boolean
  isRequesterSalarySystemForeman: boolean
  isRequesterSupervisor: boolean
}

export type Recipient = {
  id: number
  first_name: string
  last_name: string
  pivot?: Pivot
  closed_at?: null
  coordinators?: Array<Record<string, unknown>>
  doctoral_candidates?: Array<Record<string, unknown>>
  email?: string
  hy_username?: string
  isSignUpComplete?: boolean
  organization?: string
  supervisor_relationships?: Array<Record<string, unknown>>
  thesis_committee_members?: Array<Record<string, unknown>>
}

type Pivot = {
  created_at: string
  is_owner: number
  reminder_id: number
  updated_at: string
  user_id: number
}

export type DoctoralCandidate = {
  name: string
  studentNumber: string
  userId: number | null
  supervisors: SupervisorRelationship[]
  supervisorInvitations: SupervisorInvitation[]
  studyRight: {
    educationId: string
    state: string
    studyStartDate: string
    valid: {
      startDate: string
    }
  }
  isNextThesisCommitteeMeetingLate: boolean
}

export type Filter = {
  id: string
  label: string
  value: boolean
  setter: Dispatch<SetStateAction<boolean>>
}

export type UserDoctoralCandidateSupervisorRelationship = {
  id: number
  userId: number
  name: string
  roles: SupervisorRole[]
  isVerified: boolean
  verifiedAt: string
  verifiedBy: string | null
}

type UserDoctoralCandidateThesisCommitteeMember = {
  email: string
  id: number
  name: string
  userId: number
}

export type ReportForReportStatusStatistics = {
  [key: string]: any // eslint-disable-line @typescript-eslint/no-explicit-any -- react-table dynamic access
  doctoralCandidateId: number
  doctoralProgramme: string
  email: string
  latestCompletedReportDate: string | null
  name: string
  mainSupervisor: string
  mainSupervisorId: number
  reportStatus: 'report-is-late' | 'latest-report-is-not-completed' | 'reports-are-ok'
  userId: number
  verifiedStudentNumber: string | null
}

export type Row<T extends RowData> = TableRow<T>

export type Column<T extends RowData> = ColumnDefinition<T>

export type SupervisorRelationship = {
  id: number
  name: string
  email: string
  userId: number
  roles: SupervisorRole[]
  endDate: null | string
}

export type SupervisorInvitation = {
  id: number
  email: string
  roles: SupervisorRole[]
  validUntilDate: string
}

export type TranslatedString = {
  fi: string
  sv: string
  en: string
}
export type I18NLanguage = keyof TranslatedString

export type ThesisCommitteeMeeting = {
  id: number
  doctoralCandidateId: number
  date: string | null
  meetingNotes: string | null
  progressEstimation: string | null
  supportFeedback: string | null
}
